import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, FolderTree, Plus, RefreshCw, Search, X } from 'lucide-react';
import { ACCOUNT_TYPES, chartRows, chartMoney, chartQuery, chartCsv } from '../utils/canonicalChart';
import CanonicalAccountStatement from './CanonicalAccountStatement';
import './CanonicalChartOfAccounts.css';
import './CanonicalChartResponsive.css';

const API = '/api/accounts/canonical';

// Account creation is idempotent on the server and therefore always needs a
// syntactically valid UUID.  crypto.randomUUID is unavailable on some
// non-secure browser contexts (for example a temporary HTTP QA host), so keep
// the UI usable there without weakening the request contract.
export const createCanonicalRequestId = () => {
  const cryptography = typeof window !== 'undefined' ? window.crypto : undefined;
  try {
    if (typeof cryptography?.randomUUID === 'function') return cryptography.randomUUID();
    if (typeof cryptography?.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      cryptography.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch (_) {
    // Use the UUID-shaped fallback below when the browser blocks Web Crypto.
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const value = Math.floor(Math.random() * 16);
    return (character === 'x' ? value : ((value & 0x03) | 0x08)).toString(16);
  });
};

const initialForm = (parent = null) => ({
  name: '', kind: parent ? 'sub' : 'main', parent_id: parent?.id || '',
  account_type: parent?.account_type || 'expense', request_id: createCanonicalRequestId(),
});

function AccountForm({ accounts, parent, onClose, onSaved }) {
  const [form, setForm] = useState(() => initialForm(parent));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const nameRef = useRef(null);
  const dialogRef = useRef(null);
  const busyRef = useRef(false);
  const parentAccount = accounts.find(account => account.id === form.parent_id);
  const mainParents = accounts.filter(account => account.active && !account.is_posting && !account.parent_id);
  const change = (patch) => {
    setError('');
    setForm(previous => ({ ...previous, ...patch, request_id: createCanonicalRequestId() }));
  };
  useEffect(() => {
    const previous = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('canonical-account-form-open');
    nameRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove('canonical-account-form-open');
      previous?.focus?.();
    };
  }, []);
  const onKeyDown = event => {
    if (event.key === 'Escape' && !busy) onClose();
    if (event.key !== 'Tab') return;
    const controls = [...dialogRef.current.querySelectorAll('button:not(:disabled),input:not(:disabled),select:not(:disabled)')];
    const first = controls[0], last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  const submit = async event => {
    event.preventDefault();
    if (busyRef.current) return;
    if (form.kind === 'sub' && !form.parent_id) { setError('اختر الحساب الأب.'); return; }
    busyRef.current = true; setBusy(true); setError('');
    try {
      const response = await fetch(API, { method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, name: form.name.trim(), parent_id: form.kind === 'sub' ? (form.parent_id || null) : null,
          account_type: parentAccount?.account_type || form.account_type }) });
      const data = await response.json();
      if (!response.ok || !data.success || !data.account?.id) throw new Error(typeof data.detail === 'string' ? data.detail : 'تعذر حفظ الحساب.');
      onSaved(data.account);
    } catch (failure) { setError(failure.message || 'تعذر الاتصال. أعد المحاولة.'); }
    finally { busyRef.current = false; setBusy(false); }
  };
  return <div className="coa-modal-backdrop">
    <section ref={dialogRef} className="coa-modal" role="dialog" aria-modal="true" aria-labelledby="coa-form-title" onKeyDown={onKeyDown}>
      <header><h2 id="coa-form-title">إضافة حساب</h2><button type="button" className="coa-icon-button" aria-label="إغلاق" disabled={busy} onClick={onClose}><X size={20}/></button></header>
      <form onSubmit={submit}>
        <label htmlFor="coa-name">اسم الحساب</label>
        <input id="coa-name" ref={nameRef} value={form.name} disabled={busy} required minLength={2} maxLength={120} onChange={e => change({ name: e.target.value })}/>
        <label htmlFor="coa-kind">نوع الحساب</label>
        <select id="coa-kind" value={form.kind} disabled={busy} onChange={e => {
          const kind = e.target.value;
          change(kind === 'main' ? { kind, parent_id: '' } : { kind });
        }}>
          <option value="main">رئيسي</option><option value="sub">فرعي</option>
        </select>
        <label htmlFor="coa-parent">الحساب الأب{form.kind === 'main' ? ' — المستوى الأول' : ''}</label>
        <select id="coa-parent" value={form.parent_id} disabled={busy || form.kind === 'main'} required={form.kind === 'sub'} onChange={e => {
          const selected = accounts.find(account => account.id === e.target.value);
          change({ parent_id: e.target.value, ...(selected ? { account_type: selected.account_type } : {}) });
        }}>
          <option value="">{form.kind === 'main' ? 'بدون — المستوى الأول' : 'اختر الحساب الرئيسي'}</option>
          {mainParents.map(account => <option key={account.id} value={account.id}>{account.code} · {account.name_ar}</option>)}
        </select>
        <label htmlFor="coa-type">التصنيف</label>
        <select id="coa-type" value={parentAccount?.account_type || form.account_type} disabled={busy || Boolean(parentAccount)} onChange={e => change({ account_type: e.target.value })}>
          {Object.entries(ACCOUNT_TYPES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <p className="coa-form-hint">{form.kind === 'main' ? 'يجمع الحسابات التابعة له.' : 'حساب لتسجيل الحركات.'} الرمز يُنشأ تلقائيًا.</p>
        {error && <p className="coa-error" role="alert">{error}</p>}
        <footer><button type="button" className="coa-button" disabled={busy} onClick={onClose}>إلغاء</button><button className="coa-button coa-primary" disabled={busy || form.name.trim().length < 2}>{busy ? 'جارٍ الحفظ…' : 'إضافة'}</button></footer>
      </form>
    </section>
  </div>;
}

export default function CanonicalChartOfAccounts() {
  const [accounts, setAccounts] = useState([]), [canCreate, setCanCreate] = useState(false);
  const [loading, setLoading] = useState(true), [error, setError] = useState('');
  const [search, setSearch] = useState(''), [type, setType] = useState('all');
  const [expanded, setExpanded] = useState(new Set()), [form, setForm] = useState(null);
  const [notice, setNotice] = useState('');
  const [period, setPeriod] = useState({ from: '', to: '', snapshot: '' });
  const [dates, setDates] = useState({ from: '', to: '' });
  const [totals, setTotals] = useState(null), [selected, setSelected] = useState(null);
  const [nonzero, setNonzero] = useState(false);
  const generation = useRef(0);
  useEffect(() => {
    document.body.classList.add('canonical-chart-active');
    return () => document.body.classList.remove('canonical-chart-active');
  }, []);
  const load = useCallback(async (range = { from: '', to: '' }) => {
    const current = ++generation.current;
    setLoading(true); setError(''); setSelected(null); setTotals(null);
    try {
      const query = chartQuery(range).toString();
      const response = await fetch(API + (query ? `?${query}` : ''), { credentials: 'include', cache: 'no-store' });
      const data = await response.json();
      if (!response.ok || !data.success || !Array.isArray(data.accounts) || data.balances_available !== true || data.source !== 'posted_journal' || !data.period?.snapshot) throw new Error(typeof data.detail === 'string' ? data.detail : 'تعذر التحقق من أرصدة الحسابات.');
      if (generation.current !== current) return;
      setAccounts(data.accounts); setCanCreate(data.can_create === true);
      setPeriod(data.period); setTotals(data.totals);
      setDates({ from: data.period.from || '', to: data.period.to });
      setExpanded(previous => previous.size ? previous : new Set(data.accounts.filter(a => !a.parent_id).map(a => a.id)));
    } catch (failure) { if (generation.current === current) { setError(failure.message); setAccounts([]); setCanCreate(false); } }
    finally { if (generation.current === current) setLoading(false); }
  }, []);
  useEffect(() => { load(); return () => { generation.current += 1; }; }, [load]);
  const visible = useMemo(() => {
    if (!nonzero) return chartRows(accounts, search, type, expanded);
    const keep = new Set(), byId = new Map(accounts.map(a=>[a.id,a]));
    accounts.filter(a=>a.balance !== '0.00' || a.line_count > 0).forEach(a=>{
      let node=a; const seen=new Set();
      while(node && !seen.has(node.id)){seen.add(node.id);keep.add(node.id);node=byId.get(node.parent_id);}
    });
    return chartRows(accounts.filter(a=>keep.has(a.id)), search, type, expanded);
  }, [accounts, search, type, expanded, nonzero]);
  const toggle = id => setExpanded(previous => { const next = new Set(previous); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const saved = account => {
    setAccounts(previous => [...previous.filter(a => a.id !== account.id), account]);
    setExpanded(previous => {
      const next = new Set(previous), visited = new Set(); let id = account.parent_id;
      while (id && !visited.has(id)) { visited.add(id); next.add(id); id = accounts.find(a => a.id === id)?.parent_id; }
      return next;
    });
    setSearch(''); setType('all'); setNonzero(false); setForm(null); setNotice(`تمت إضافة «${account.name_ar}» — ${account.code}`);
    load({ from: period.from, to: period.to });
  };
  const exportChart = () => {
    const blob = new Blob([chartCsv(accounts, period)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob), anchor = document.createElement('a');
    anchor.href = url; anchor.download = `دليل-الحسابات-${period.to}.csv`; anchor.click();
    setTimeout(()=>URL.revokeObjectURL(url), 1000);
  };
  return <main className="canonical-coa" dir="rtl" data-testid="canonical-chart-page">
    <header className="coa-heading"><div><div className="coa-eyebrow"><FolderTree size={18}/> المحاسبة</div><h1>دليل الحسابات</h1><p>الأرصدة وكشوف الحركة للحسابات الرئيسية والفرعية.</p></div>
      <div className="coa-heading-actions"><button className="coa-icon-button" aria-label="تحديث الدليل" disabled={loading} onClick={()=>load({ from: period.from, to: period.to })}><RefreshCw size={19}/></button>
        <button className="coa-button" disabled={loading || !totals} onClick={exportChart}>تصدير</button>
        {canCreate && <button className="coa-button coa-primary" onClick={() => { setNotice(''); setForm({ parent: null }); }}><Plus size={18}/> إضافة حساب</button>}</div>
    </header>
    <form className="coa-period" onSubmit={event=>{event.preventDefault();load(dates);}}>
      <label>من<input type="date" aria-label="من تاريخ" value={dates.from} max={dates.to || undefined} onChange={e=>setDates({...dates,from:e.target.value})}/></label>
      <label>إلى<input type="date" aria-label="إلى تاريخ" value={dates.to} min={dates.from || undefined} required onChange={e=>setDates({...dates,to:e.target.value})}/></label>
      <button type="submit" className="coa-button" disabled={loading}>عرض</button><button type="button" className="coa-button" disabled={loading} onClick={()=>load()}>من البداية</button>
    </form>
    {!loading && totals && <div className="coa-summary">{Object.entries(ACCOUNT_TYPES).map(([key,label])=><div className={`coa-summary-item coa-${key}`} key={key}><small>{label}</small><strong><bdi>{chartMoney(totals[key])}</bdi></strong></div>)}</div>}
    <div className="coa-toolbar"><label className="coa-search"><Search size={18}/><input aria-label="البحث في الحسابات" placeholder="اسم الحساب أو الرمز" value={search} onChange={e => setSearch(e.target.value)}/></label>
      <select aria-label="تصنيف الحسابات" value={type} onChange={e => setType(e.target.value)}><option value="all">كل التصنيفات</option>{Object.entries(ACCOUNT_TYPES).map(([value,label]) => <option value={value} key={value}>{label}</option>)}</select>
    </div>
    <div className="coa-caption"><span>{accounts.length} حسابًا</span><label><input type="checkbox" checked={nonzero} onChange={e=>setNonzero(e.target.checked)}/> ذات رصيد أو حركة</label><button onClick={() => setExpanded(new Set(accounts.map(a => a.id)))}>توسيع الكل</button><button onClick={() => setExpanded(new Set())}>طي الكل</button></div>
    {notice && <p className="coa-success" role="status">{notice}</p>}
    {loading ? <p role="status" className="coa-state">جارٍ تحميل الحسابات…</p> : error ? <p role="alert" className="coa-error">{error}</p> :
      <section className="coa-tree" aria-label="شجرة الحسابات">{visible.length ? visible.map(account => <article key={account.id} className={`coa-row coa-${account.account_type} ${!account.active ? 'coa-inactive' : ''}`} data-testid={`account-${account.code}`} style={{ '--depth': Math.min(account.depth, 4) }}>
        <div className={`coa-row-content ${canCreate && account.active ? '' : 'coa-readonly-row'}`}>
          {account.childCount ? <button className="coa-tree-toggle" aria-label={`${account.expanded ? 'طي' : 'توسيع'} ${account.name_ar}`} aria-expanded={account.expanded} onClick={() => toggle(account.id)}>{account.expanded ? <ChevronDown size={18}/> : <ChevronLeft size={18}/>}</button> : <span className="coa-tree-dot"/>}
          <div className="coa-account"><div><bdi className="coa-code">{account.code}</bdi><button className="coa-account-name" aria-label={`كشف ${account.name_ar}`} onClick={()=>setSelected(account)}>{account.name_ar}</button></div><small>{ACCOUNT_TYPES[account.account_type]} · {account.is_posting ? 'فرعي' : 'رئيسي'}{!account.active ? ' · غير مفعل' : ''}</small></div>
          <button className="coa-balance" aria-label={`رصيد ${account.name_ar}`} onClick={()=>setSelected(account)}><bdi>{chartMoney(account.balance)}</bdi><small>{account.descendant_count > 0 ? 'شامل الفروع' : account.line_count ? 'كشف الحركة' : 'بلا حركة في الفترة'}</small></button>
          {canCreate && account.active && !account.is_posting && !account.parent_id && <button className="coa-add-child" aria-label={`إضافة حساب تحت ${account.name_ar}`} onClick={() => setForm({ parent: account })}><Plus size={17}/><span>فرعي</span></button>}
        </div>
      </article>) : <p className="coa-state">لا توجد حسابات مطابقة.</p>}</section>}
    <p className="coa-note">أرصدة القيود المرحلة حتى {period.to || 'التاريخ المحدد'}. الحساب الرئيسي يشمل فروعه، والملخص يحسب كل حركة مرة واحدة. السالب عكس طبيعة الحساب.</p>
    {form && <AccountForm accounts={accounts} parent={form.parent} onClose={() => setForm(null)} onSaved={saved}/>}
    {selected && <CanonicalAccountStatement account={selected} period={period} onClose={()=>setSelected(null)}/>}
  </main>;
}
